import { useEffect, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { billingApiService } from '../services/billingApiService'
import { ApiError } from '../services/BaseApiService'
import { useAuth } from '../contexts/AuthContext'
import { SecurityRole } from '../constants'
import { config } from '../config/environment'
import { BILLING_PLAN_NAMES, type BillingStatus } from '../types'
import CurrentBill, { useCurrentBill } from '../components/billing/CurrentBill'
import ThisMonth from '../components/billing/ThisMonth'
import { ConfirmModal } from '../components/ui/ConfirmModal'
import PlanChange from '../components/billing/PlanChange'
import { DEFAULT_OVERAGE_LIMIT_DOLLARS, MAX_OVERAGE_LIMIT_DOLLARS, extraPagesFor, formatCents } from '../utils/billingStanding'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'

/**
 * Billing: the card on file, this month's usage, and what has been charged.
 *
 * A team is on a published plan (Standard / Enhanced / Ultimate: a monthly minimum with included pages,
 * then a per-page rate) or on Free, which DocMorphia assigns to special teams: Standard's allowance and
 * features, never charged, so its usage against the allowance is shown for information only.
 *
 * Every figure here comes from our own tables. The payment provider is called only to collect the
 * card, so this page renders whether or not Stripe is reachable.
 */

const stripePromise = config.stripePublishableKey ? loadStripe(config.stripePublishableKey) : null

const card = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  boxShadow: shadow.sm,
  padding: spacing.xl,
  marginBottom: spacing.lg,
}

/** Collects a card and registers it. Card details go straight to Stripe.js and never touch us. */
function CardForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    const element = elements.getElement(CardElement)
    if (!element) return

    setError('')
    setSaving(true)
    try {
      const { clientSecret } = await billingApiService.setupIntent()
      const result = await stripe.confirmCardSetup(clientSecret, { payment_method: { card: element } })

      if (result.error) {
        setError(result.error.message ?? 'The card could not be saved')
        return
      }

      // The SetupIntent holds the payment method id once confirmed. Registering it server-side is
      // what caches the brand and last four, so this page never has to ask Stripe again.
      const paymentMethodRef = result.setupIntent?.payment_method
      if (typeof paymentMethodRef !== 'string') {
        setError('The card was saved but could not be linked to this team')
        return
      }

      await billingApiService.attachPaymentMethod(paymentMethodRef)
      onSuccess()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The card could not be saved')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.md, marginBottom: spacing.md }}>
        <CardElement options={{ style: { base: { fontSize: '15px', color: colors.text } } }} />
      </div>
      {error && <div style={{ color: colors.error, fontSize: fontSize.sm, marginBottom: spacing.sm }}>{error}</div>}
      <button
        type="submit"
        disabled={!stripe || saving}
        style={{
          padding: '8px 16px', border: 'none', borderRadius: radius.sm,
          background: colors.primary, color: colors.onPrimary,
          fontSize: fontSize.sm, fontWeight: 600, cursor: saving ? 'default' : 'pointer',
        }}
      >
        {saving ? 'Saving…' : 'Save card'}
      </button>
    </form>
  )
}

/**
 * Adds a US bank account (ACH Direct Debit). Stripe.js collects the account (instant via Financial
 * Connections, or routing/account for micro-deposits) and the ACH mandate; our server records it. A
 * micro-deposit account comes back needing the two amounts, which the customer enters 1–2 days later.
 */
function BankAccountForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe()
  const { user } = useAuth()
  const [phase, setPhase] = useState<'idle' | 'microdeposits' | 'done'>('idle')
  const [setupIntentId, setSetupIntentId] = useState('')
  const [a1, setA1] = useState('')
  const [a2, setA2] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  async function addBank() {
    if (!stripe) return
    setBusy(true); setError(''); setNotice('')
    try {
      const { clientSecret, setupIntentId: sid } = await billingApiService.bankAccountSetupIntent()
      const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Account holder'
      const collect = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: { payment_method_type: 'us_bank_account', payment_method_data: { billing_details: { name, email: user?.email ?? undefined } } },
        expand: ['payment_method'],
      })
      if (collect.error) { setError(collect.error.message ?? 'Could not collect the bank account'); return }
      // Presents and captures the ACH mandate, then confirms the setup.
      const confirmed = await stripe.confirmUsBankAccountSetup(clientSecret)
      if (confirmed.error) { setError(confirmed.error.message ?? 'Could not confirm the bank account'); return }

      const res = await billingApiService.confirmBankAccount(sid)
      if (res.requiresMicrodeposits) {
        setSetupIntentId(sid)
        setPhase('microdeposits')
        setNotice('Stripe is sending two small deposits to your account. Enter the amounts here once they arrive (1–2 business days).')
      } else {
        setPhase('done'); onSuccess()
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the bank account')
    } finally { setBusy(false) }
  }

  async function verify() {
    setBusy(true); setError('')
    try {
      await billingApiService.verifyMicrodeposits(setupIntentId, [Number(a1), Number(a2)])
      setPhase('done'); onSuccess()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Those amounts did not match. Try again.')
    } finally { setBusy(false) }
  }

  if (phase === 'microdeposits') {
    return (
      <div>
        {notice && <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.sm }}>{notice}</div>}
        <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', marginBottom: spacing.sm }}>
          <input inputMode="numeric" placeholder="e.g. 32" value={a1} onChange={e => setA1(e.target.value)}
            style={{ width: 80, padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm }} />
          <span style={{ color: colors.textSecondary }}>¢ and</span>
          <input inputMode="numeric" placeholder="e.g. 45" value={a2} onChange={e => setA2(e.target.value)}
            style={{ width: 80, padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm }} />
          <button type="button" disabled={busy || !a1 || !a2} onClick={verify}
            style={{ padding: '8px 16px', border: 'none', borderRadius: radius.sm, background: colors.primary, color: colors.onPrimary, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}>
            {busy ? 'Verifying…' : 'Verify'}
          </button>
        </div>
        {error && <div style={{ color: colors.error, fontSize: fontSize.sm }}>{error}</div>}
      </div>
    )
  }

  return (
    <div>
      <button type="button" disabled={busy} onClick={addBank}
        style={{ padding: '8px 16px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.text, fontSize: fontSize.sm, cursor: 'pointer' }}>
        {busy ? 'Starting…' : 'Pay by bank (ACH)'}
      </button>
      {error && <div style={{ color: colors.error, fontSize: fontSize.sm, marginTop: spacing.sm }}>{error}</div>}
    </div>
  )
}

/**
 * The team's overage settings (V084). Overage billing is on by default, with a monthly limit (default $25) on what pages
 * past the allowance may add to a bill: renders that would pass it are refused, and the month's overage line never
 * bills above it. Blank means no limit. Turning overage off stops renders at the included pages; the limit is kept for
 * when it is turned back on. Hidden for Free and billing-exempt teams, which are never charged.
 */
function OverageSetting({ status, onChanged }: { status: BillingStatus; onChanged: () => void }) {
  const allow = status.allowOverage !== false
  // Prefilled with the stored limit; $25 (the default) when the status does not carry one. Blank is "no limit".
  const stored = status.overageLimitDollars === undefined ? DEFAULT_OVERAGE_LIMIT_DOLLARS : status.overageLimitDollars
  const canChange = status.canManageBilling === true
  const [limitText, setLimitText] = useState(stored == null ? '' : String(stored))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => { setLimitText(stored == null ? '' : String(stored)) }, [stored])

  const rate = status.perPageCents != null ? `${formatCents(status.perPageCents)} per page` : "the plan's per-page rate"
  const included = (status.includedPages ?? 0).toLocaleString()
  const trimmed = limitText.trim()
  const parsed = trimmed === '' ? null : /^\d+$/.test(trimmed) ? Number(trimmed) : NaN
  const valid = parsed === null || (Number.isInteger(parsed) && parsed >= 1 && parsed <= MAX_OVERAGE_LIMIT_DOLLARS)
  const dirty = valid && parsed !== stored
  const extra = parsed != null && valid ? extraPagesFor(parsed, status.perPageCents) : null

  async function save(nextAllow: boolean, nextLimit: number | null) {
    if (saving) return
    setSaving(true); setError('')
    try {
      await billingApiService.setOverageSettings(nextAllow, nextLimit)
      onChanged()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The setting could not be saved')
    } finally { setSaving(false) }
  }

  const hint = { display: 'block', fontSize: fontSize.sm, color: colors.textSecondary } as const

  return (
    <div style={card} data-testid="overage-setting">
      <div style={{ fontSize: fontSize.lg, fontWeight: 600, marginBottom: spacing.sm }}>When included pages run out</div>
      <label style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-start', marginBottom: spacing.sm, cursor: canChange ? 'pointer' : 'default' }}>
        <input type="checkbox" checked={allow} disabled={!canChange || saving} data-testid="overage-allow"
          onChange={e => void save(e.target.checked, e.target.checked ? (valid ? parsed : stored) : stored)} />
        <span>
          <span style={{ fontWeight: 600, fontSize: fontSize.sm }}>Allow overage billing</span>
          <span style={hint}>
            {allow
              ? `Renders continue past your ${included} included pages; the pages beyond are billed at ${rate}.`
              : `Off: renders that would go past ${included} pages this month are refused until next month or until you turn this back on. Nothing beyond the monthly minimum is billed for pages.`}
          </span>
        </span>
      </label>
      {allow && (
        <div style={{ marginLeft: spacing.lg }} data-testid="overage-limit">
          <label htmlFor="overage-limit-input" style={{ fontWeight: 600, fontSize: fontSize.sm }}>Monthly overage limit</label>
          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', marginTop: spacing.xs }}>
            <span>$</span>
            <input id="overage-limit-input" type="text" inputMode="numeric" value={limitText} placeholder="No limit"
              disabled={!canChange || saving} data-testid="overage-limit-input" aria-invalid={!valid}
              onChange={e => setLimitText(e.target.value)}
              style={{ width: 120, padding: spacing.xs, border: `1px solid ${valid ? colors.border : colors.error}`, borderRadius: radius.sm }} />
            {canChange && (
              <button type="button" disabled={!dirty || saving} data-testid="overage-limit-save" onClick={() => void save(true, parsed)}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
          <span style={{ ...hint, marginTop: spacing.xs }} data-testid="overage-limit-hint">
            {!valid
              ? `Enter whole dollars from $1 to $${MAX_OVERAGE_LIMIT_DOLLARS.toLocaleString()}, or leave it blank for no limit.`
              : parsed == null
                ? 'No limit: every page past your included pages is billed.'
                : extra != null
                  ? `About ${extra.toLocaleString()} extra pages at your rate. Renders that would go past the limit are refused, and page overage never bills above it.`
                  : 'Renders that would go past the limit are refused, and page overage never bills above it.'}
          </span>
        </div>
      )}
      {!canChange && <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>A team admin or a member with Manage billing can change this.</div>}
      {error && <div style={{ color: colors.error, fontSize: fontSize.sm, marginTop: spacing.sm }}>{error}</div>}
    </div>
  )
}

function BillingContent() {
  const { user } = useAuth()
  const isAdmin = (user?.securityRole ?? 0) >= SecurityRole.TEAM_ADMIN
  const [status, setStatus] = useState<BillingStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [addingCard, setAddingCard] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [removing, setRemoving] = useState(false)
  // Bumped on every reload so the current bill (spend, forecast, statement) refreshes with the status.
  const [billKey, setBillKey] = useState(0)
  const bill = useCurrentBill(billKey)

  async function load() {
    setLoading(true)
    setBillKey(k => k + 1)
    try {
      setStatus(await billingApiService.getStatus())
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load billing')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  async function requestCustomPricing() {
    try {
      await billingApiService.requestCustomPricing()
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The request could not be sent')
    }
  }

  async function removeCard() {
    setRemoving(true)
    try {
      await billingApiService.removePaymentMethod()
      setConfirmRemove(false)
      await load()
    } catch (err) {
      setConfirmRemove(false)
      setError(err instanceof ApiError ? err.message : 'The card could not be removed')
    } finally {
      setRemoving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textSecondary }}>Loading…</div>

  const planName = BILLING_PLAN_NAMES[status?.planCode ?? ''] ?? 'Standard'

  return (
    <div style={{ maxWidth: 820 }}>
      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{error}</div>
      )}

      {status && status.requiresPaymentMethod && !status.hasPaymentMethod && !status.isSuspended && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>
          Your {planName} plan needs a payment method. Rendering and saving templates are paused until
          one is added — your plan and settings are unchanged.
        </div>
      )}

      {status?.isSuspended && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>
          Rendering is suspended because an outstanding balance could not be collected.
          Update the card below to resume.
        </div>
      )}

      {status && <ThisMonth status={status} bill={bill.data?.current ?? null} billLoading={!bill.data && !bill.forbidden && !bill.error} />}

      <div style={card}>
        <div style={{ fontSize: fontSize.lg, fontWeight: 600, marginBottom: spacing.md }}>Payment method</div>

        {status && status.billingAvailable === false ? (
          <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>
            Billing is not configured in this environment.
          </div>
        ) : status?.paymentMethod ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
            <div style={{ fontSize: fontSize.base }}>
              {status.paymentMethod.brand ?? 'Card'} ending {status.paymentMethod.last4 ?? '••••'}
              {status.paymentMethod.expiryMonth && status.paymentMethod.expiryYear && (
                <span style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>
                  {' '}· expires {status.paymentMethod.expiryMonth}/{status.paymentMethod.expiryYear}
                </span>
              )}
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setConfirmRemove(true)}
                style={{
                  marginLeft: 'auto', padding: '6px 12px', border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm, background: 'none', color: colors.textSecondary,
                  fontSize: fontSize.sm, cursor: 'pointer',
                }}
              >
                Remove
              </button>
            )}
          </div>
        ) : addingCard && stripePromise ? (
          <Elements stripe={stripePromise}>
            <CardForm onSuccess={() => { setAddingCard(false); void load() }} />
          </Elements>
        ) : addingCard && !stripePromise ? (
          <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>
            Card payments are not configured in this environment.
          </div>
        ) : !isAdmin ? (
          <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>
            No payment method on file. Only a team admin can add one.
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddingCard(true)}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: radius.sm,
              background: colors.primary, color: colors.onPrimary,
              fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Add a card
          </button>
        )}

        {isAdmin && status?.billingAvailable !== false && stripePromise && (
          <div style={{ marginTop: spacing.md, paddingTop: spacing.md, borderTop: `1px solid ${colors.border}` }}>
            <Elements stripe={stripePromise}>
              <BankAccountForm onSuccess={() => void load()} />
            </Elements>
            <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: spacing.sm }}>
              Bank payments (ACH) clear in a few business days and cost less than cards for larger invoices.
            </div>
          </div>
        )}
      </div>

      {status?.appliesOverageSettings && status.includedPages != null && (
        <OverageSetting status={status} onChanged={() => void load()} />
      )}

      {/* This month's itemised bill and past bills (itemised for Manage billing; totals for everyone else). */}
      <CurrentBill periods={status?.periods ?? []} load={bill} />

      <div style={card}>
        <div style={{ fontSize: fontSize.lg, fontWeight: 600, marginBottom: spacing.md }}>Plan</div>
        <div style={{ fontSize: fontSize.base, marginBottom: spacing.md }}>
          Current: <strong>{planName}</strong>
        </div>
        {status?.billingAvailable !== false && <PlanChange onChanged={() => void load()} overage={status ? {
          allowOverage: status.allowOverage,
          overageLimitDollars: status.overageLimitDollars === undefined ? DEFAULT_OVERAGE_LIMIT_DOLLARS : status.overageLimitDollars,
        } : undefined} />}
      </div>

      {/* Offered on Ultimate too, unlike the old Enterprise ask: every plan is self-served now, so this
          is for a team whose volume has outgrown even Ultimate's published rate. */}
      <div style={card}>
        <div style={{ fontSize: fontSize.lg, fontWeight: 600, marginBottom: spacing.sm }}>
          Higher volume
        </div>
        <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
          Rendering more than Ultimate's allowance every month? We will price your volume directly.
          That is a conversation rather than a switch, so this sends us a note.
        </div>
        {status?.customPricingRequestedAt ? (
          <div style={{ fontSize: fontSize.sm, color: colors.success }}>
            Requested {new Date(status.customPricingRequestedAt).toLocaleDateString()} — we will be in touch.
          </div>
        ) : isAdmin ? (
          <button
            type="button"
            onClick={requestCustomPricing}
            style={{
              padding: '8px 16px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
              background: 'none', color: colors.text, fontSize: fontSize.sm, cursor: 'pointer',
            }}
          >
            Ask about custom pricing
          </button>
        ) : (
          <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>A team admin can request custom pricing.</div>
        )}
      </div>

      <ConfirmModal
        open={confirmRemove}
        title="Remove the card on file?"
        message="Your plan is unchanged, but you will not be able to render documents or save templates until a card is added. Documents already rendered this month will still be billed."
        confirmLabel="Remove card"
        danger
        loading={removing}
        onConfirm={() => void removeCard()}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  )
}

export default function BillingPage() {
  return (
    <div>
      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, marginBottom: spacing.xl }}>
        Billing
      </h1>
      <BillingContent />
    </div>
  )
}
